import { useId } from 'react'
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { cx } from './cx'

const CONTROL =
  'border rounded-lg text-gray-900 bg-white placeholder-gray-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

/**
 * Same two sizes as Button. `sm` exists for controls edited inline inside a
 * dense row — a caller cannot shrink the default from outside, because padding
 * and text-size utilities are decided by Tailwind's emit order rather than by
 * the order they appear in a className string.
 */
export type ControlSize = 'sm' | 'md'

const SIZE: Record<ControlSize, string> = {
  sm: 'px-2 py-1 text-xs',
  md: 'px-3 py-2 text-sm',
}

// `w-full` is applied via the fullWidth prop, not baked into CONTROL: a caller
// cannot cancel it with `w-auto`, because Tailwind emits w-full after w-auto and
// they have equal specificity.
const ok = 'border-gray-300'
const bad = 'border-red-300'

/**
 * Label + control + hint/error, with the label wired to the control by id.
 * Pass a single form control as the child; it receives the generated id.
 */
export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: {
  label?: ReactNode
  hint?: ReactNode
  error?: string
  required?: boolean
  className?: string
  /** Receives the generated id + aria-describedby. */
  children: (props: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => ReactNode
}) {
  const id = useId()
  const msgId = `${id}-msg`
  const hasMsg = Boolean(error || hint)
  return (
    <div className={cx('space-y-1', className)}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-600 ml-0.5">*</span>}
        </label>
      )}
      {children({
        id,
        'aria-describedby': hasMsg ? msgId : undefined,
        'aria-invalid': error ? true : undefined,
      })}
      {hasMsg && (
        <p id={msgId} className={cx('text-xs', error ? 'text-red-600' : 'text-gray-500')}>
          {error || hint}
        </p>
      )}
    </div>
  )
}

export function Input({
  invalid,
  fullWidth = true,
  size = 'md',
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  invalid?: boolean
  fullWidth?: boolean
  size?: ControlSize
}) {
  return <input className={cx(CONTROL, SIZE[size], fullWidth && 'w-full', invalid ? bad : ok, className)} {...rest} />
}

export function Select({
  invalid,
  fullWidth = true,
  size = 'md',
  className,
  children,
  ...rest
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
  invalid?: boolean
  fullWidth?: boolean
  size?: ControlSize
}) {
  return (
    <select className={cx(CONTROL, SIZE[size], fullWidth && 'w-full', invalid ? bad : ok, className)} {...rest}>
      {children}
    </select>
  )
}

export function Textarea({
  invalid,
  fullWidth = true,
  size = 'md',
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  invalid?: boolean
  fullWidth?: boolean
  size?: ControlSize
}) {
  return <textarea className={cx(CONTROL, SIZE[size], fullWidth && 'w-full', invalid ? bad : ok, className)} {...rest} />
}

/** Checkbox with its label, wired together. */
export function Checkbox({
  label,
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: ReactNode }) {
  const id = useId()
  return (
    <label htmlFor={id} className={cx('flex items-center gap-2 text-sm text-gray-700', className)}>
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
        {...rest}
      />
      {label}
    </label>
  )
}
